import type { FC } from 'react'
import type { PickupAvailability } from '@/types'

import { RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDateKey } from '@/lib/hours'

interface PickupPickerProps {
    idPrefix: string
    availability: PickupAvailability | null
    state: 'loading' | 'ready' | 'error'
    date: string | null
    time: string | null
    onDateChange: (date: string) => void
    onTimeChange: (time: string) => void
    onRetry: () => void
    dateError?: string
    timeError?: string
}

export const PickupPicker: FC<PickupPickerProps> = ({
    idPrefix,
    availability,
    state,
    date,
    time,
    onDateChange,
    onTimeChange,
    onRetry,
    dateError,
    timeError
}) => {
    if (state === 'error') {
        return (
            <div className='border border-line px-5 py-6 text-[0.9375rem]' role='alert'>
                <p>همین حالا نتوانستیم زمان‌های تحویل را بارگذاری کنیم.</p>

                <button
                    type='button'
                    onClick={onRetry}
                    className='mt-3 inline-flex cursor-pointer items-center gap-2 text-sm font-medium underline decoration-line-strong underline-offset-4 hover:decoration-foreground focus-visible:outline-2 focus-visible:outline-amber'
                >
                    <RotateCcw className='size-4' aria-hidden='true' />
                    تلاش دوباره
                </button>
            </div>
        )
    }

    if (state === 'loading' && !availability) {
        return (
            <div aria-busy='true'>
                <div className='grid grid-cols-3 gap-2'>
                    {Array.from({ length: 7 }, (_, index) => (
                        <div key={index} className='h-[5.25rem] animate-pulse rounded-sm bg-flour-deep' />
                    ))}
                </div>

                <p className='mt-3 text-sm text-crust-soft'>در حال بارگذاری زمان‌های تحویل</p>
            </div>
        )
    }

    const days = availability?.days ?? []
    const selectedDay = days.find(day => day.date === date)

    return (
        <div className='space-y-6'>
            <fieldset aria-describedby={dateError ? `${idPrefix}-date-error` : undefined}>
                <legend className='label-caps text-crust-soft'>روز تحویل</legend>

                <div className='mt-3 grid grid-cols-3 gap-2'>
                    {days.map((day, index) => {
                        const disabled = day.status !== 'open'
                        const checked = day.date === date

                        return (
                            <label key={day.date} className={cn('relative block', disabled ? 'cursor-not-allowed' : 'cursor-pointer')} title={day.note ?? day.label}>
                                <input
                                    type='radio'
                                    name={`${idPrefix}-day`}
                                    value={day.date}
                                    checked={checked}
                                    disabled={disabled}
                                    onChange={() => onDateChange(day.date)}
                                    className='peer sr-only'
                                    aria-label={`${day.label}${day.note ? `، ${day.note}` : ''}`}
                                />

                                <span
                                    className={cn(
                                        'flex h-[5.25rem] w-full flex-col items-center justify-between rounded-sm border py-2.5 text-center transition-colors duration-300 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-amber',
                                        checked && 'border-foreground bg-foreground text-background',
                                        !checked && !disabled && 'border-line-strong hover:border-foreground',
                                        disabled && 'border-dashed border-line bg-transparent text-crust-soft/55'
                                    )}
                                    aria-hidden='true'
                                >
                                    <span className='text-[0.6875rem] font-medium tracking-[0.12em] uppercase'>
                                        {formatDateKey(day.date, { weekday: 'short' })}
                                    </span>

                                    <span className='text-[1.375rem] leading-none font-medium tabular-nums lining-nums'>{day.day}</span>

                                    <span className={cn('text-[0.625rem] leading-none tracking-[0.08em] uppercase', checked ? 'text-background/75' : 'text-crust-soft', disabled && 'text-crust-soft/55')}>
                                        {disabled ? (day.status === 'closed' ? 'تعطیل' : 'تکمیل') : index === 0 ? 'امروز' : day.month}
                                    </span>
                                </span>
                            </label>
                        )
                    })}
                </div>

                {selectedDay?.note && <p className='mt-2 text-sm text-crust-soft'>{selectedDay.note}</p>}
                {dateError && <p id={`${idPrefix}-date-error`} className='mt-2 text-sm text-destructive'>{dateError}</p>}
            </fieldset>

            <fieldset aria-describedby={timeError ? `${idPrefix}-time-error` : undefined}>
                <legend className='label-caps text-crust-soft'>زمان تحویل</legend>

                {selectedDay && <p className='mt-1.5 font-heading text-xl leading-tight'>{selectedDay.label}</p>}

                {!selectedDay ? (
                    <p className='mt-3 text-sm text-crust-soft'>یک روز انتخاب کنید تا زمان‌های آزاد را ببینید.</p>
                ) : selectedDay.slots.length === 0 ? (
                    <p className='mt-3 text-sm text-crust-soft'>برای این روز زمان تحویلی باقی نمانده است. لطفاً روز دیگری انتخاب کنید.</p>
                ) : (
                    <div className='mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4 lg:grid-cols-3 xl:grid-cols-4'>
                        {selectedDay.slots.map(slot => {
                            const checked = slot.time === time

                            return (
                                <label key={slot.time} className='relative block cursor-pointer'>
                                    <input
                                        type='radio'
                                        name={`${idPrefix}-time`}
                                        value={slot.time}
                                        checked={checked}
                                        onChange={() => onTimeChange(slot.time)}
                                        className='peer sr-only'
                                    />

                                    <span
                                        className={cn(
                                            'flex h-11 items-center justify-center rounded-sm border text-sm tabular-nums transition-colors duration-300 peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-amber',
                                            checked ? 'border-foreground bg-foreground text-background' : 'border-line-strong hover:border-foreground'
                                        )}
                                    >
                                        {slot.label}
                                    </span>
                                </label>
                            )
                        })}
                    </div>
                )}

                {timeError && <p id={`${idPrefix}-time-error`} className='mt-2 text-sm text-destructive'>{timeError}</p>}
            </fieldset>
        </div>
    )
}
